import React from 'react';
import { useLocation } from 'react-router-dom';

import Button from '../../../../library/button/button';
import Message from '../../../message/message';

import {
  useNavigateTo,
  useProductSearchNavigation
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics.js';

import { Fitment } from '../../../../shared/types/fitment';
import { Maybe } from '../../../../shared/types/graphql-types';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';
import { SEARCH_TYPES } from '../../../../shared/constants/search-types';

import './fit-message.scss';

interface FitMessageProps {
  doesFit?: boolean;
  fitment: Fitment;
  isLoading?: boolean;
  isPackage?: boolean;
  isSingular?: boolean;
  isTextSearch?: boolean;
  productType?: string;
}

function FitMessage(props: FitMessageProps) {
  const {
    doesFit = false,
    fitment,
    isLoading = false,
    isPackage = false,
    isSingular = false,
    isTextSearch = false,
    productType = PRODUCT_TYPES.TIRES
  } = props;

  const location = useLocation();
  const navigateTo = useNavigateTo();
  const { onSearchTypeSelected, onShopAllAccessories, onShopAllTires } =
    useProductSearchNavigation({
      fitment
    });

  let productMessage = 'tires';
  let fitmentActionMessage: Maybe<string> = '';
  let isWarning = false;
  let isInfo = false;
  let isError = false;
  let isSuccess = false;
  let message = '';
  let fitmentAction: Maybe<() => void> = null;

  function handleEnterYourVehicleClick() {
    const locationHash = '#/fitment/vehicle/continue-to/close';
    const url = new URL(window.location.href);
    trackAction('product_fitment_validation_banner');
    if (Number(url.searchParams.get('page')) !== 0) {
      url.hash = locationHash;
      url.searchParams.set('page', '0');
      window.location.assign(url.toString());
    } else {
      window.location.hash = locationHash;
    }
  }

  function handleShopProductsThatFitClick() {
    trackAction('product_fitment_validation');
    if (productType === PRODUCT_TYPES.TIRES) {
      onShopAllTires();
    } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
      onShopAllAccessories();
    } else {
      onSearchTypeSelected(SEARCH_TYPES.WHEELS);
    }
  }

  if (PRODUCT_TYPES[productType] === PRODUCT_TYPES.ACCESSORIES) {
    productMessage = 'products';
  } else if (PRODUCT_TYPES[productType] === PRODUCT_TYPES.WHEELS) {
    productMessage = 'wheels';
  }

  if (!fitment) {
    fitmentActionMessage = 'Enter your vehicle';
    fitmentAction = handleEnterYourVehicleClick;
    isWarning = true;
    message = `to ensure these ${productMessage} fit`;
  }

  if (!isLoading && fitment) {
    if (doesFit || isPackage) {
      fitmentAction = null;
      fitmentActionMessage = null;
      isSuccess = true;
      message = isPackage
        ? 'This package fits your vehicle'
        : `${
            isSingular ? 'This fits' : `These ${productMessage} fit`
          } your vehicle`;
      if (isTextSearch) {
        fitmentAction = () => {
          navigateTo(`${location.pathname}${location.search}&isSearchAll=true`);
        };
        fitmentActionMessage = 'view all results for this search';
      } else {
        fitmentAction = null;
        fitmentActionMessage = null;
      }
    } else if (productType === PRODUCT_TYPES.TIRES && !doesFit) {
      isWarning = true;
      message = 'These tires may not fit your vehicle.';
    } else {
      fitmentAction = handleShopProductsThatFitClick;
      fitmentActionMessage = `shop ${productMessage} that fit`;
      isError = true;
      message = `${
        isSingular
          ? `This ${productMessage} does not`
          : `These ${productMessage} don't`
      } fit your vehicle`;
    }
  }

  return (
    <div className="fit-message" styleName="container">
      <Message
        isAlignCenter
        isError={isError}
        isInfo={isInfo}
        isSuccess={isSuccess}
        isWarning={isWarning}
        message={
          <>
            {' '}
            {message && (
              <>
                {!fitment && fitmentAction && (
                  <Button onClick={fitmentAction} weight="bold">
                    {fitmentActionMessage}
                  </Button>
                )}{' '}
                {message}
                {fitment && fitmentAction && (
                  <Button onClick={fitmentAction} styleName="button">
                    {fitmentActionMessage}
                  </Button>
                )}
              </>
            )}
          </>
        }
      />
    </div>
  );
}

export default FitMessage;
